import React, {
	useState, useMemo, useEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import TellyCard from '../../../../images/telly-card.png';
import Socket from '../../../../connection/socket';
import { GetRequiresApproval, CheckJoinRequest } from '../../../../connection/join-request';
import Settings from '../../../../socket-listeners/settings';
import Broadcast from '../../../../socket-listeners/broadcasting';
import HostSync from '../../../../socket-listeners/host-sync';
import Shots from '../../../../socket-listeners/shots';
import Users from '../../../../socket-listeners/users';
import Graphics from '../../../../socket-listeners/graphics';
import { GreenroomState } from '../../../../stores/types/types';
import { HOME_ROUTES } from '../../../../stores/reducers/home';
import { setSettingsReducerSocket } from '../../../../stores/reducers/settings';
import { setShotReducerSocket, SHOT_THUMBNAIL_TYPES } from '../../../../stores/reducers/shots';
import { CustomRtmpData } from '../../../../stores/actions/broadcasting';
import * as SessionActions from '../../../../stores/actions/session';
import * as InvitationActions from '../../../../stores/actions/invitations';
import * as SettingsActions from '../../../../stores/actions/settings';
import * as HomeActions from '../../../../stores/actions/home';
import * as BroadcastActions from '../../../../stores/actions/broadcasting';
import * as MediaActions from '../../../../stores/actions/media';
import * as ShotsActions from '../../../../stores/actions/shots';
import * as GraphicsActions from '../../../../stores/actions/graphics';
import { showDetailsBack } from '../../../../stores/actions/main';
import { cardBackground } from '../../../../utils/utils';
import WaitingIndicator from '../../../general-components/waiting-indicator';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import EventCard from '../../../general-components/event-card';
import ShowDetailsInfo from './show-details-info';
import ShowDetailsSettings from './show-details-settings';
import ShotThumbnail from '../../../general-components/shot-thumbnail';
import LoadingSpinner from '../../../general-components/loading-spinner';
import useHome from '../../../../hooks/use-home';
import useJoin from '../../../../hooks/use-join';
import { APP } from '../../../../stores/session-status';
import useMediaDevices from '../../../../hooks/use-media-devices';
import { Meeting } from '../../../../stores/reducers/session';
import useLoad from '../../../../hooks/use-load';

interface Props {
	connected: boolean;
	socket: Socket | null;
}

const SHOW_DETAILS_ROUTES = {
	INFO: 0,
	RECORDINGS: 1,
	SETTINGS: 2,
};

export default function ShowDetails(props: Props) {
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const selectedEvent = useSelector((state: GreenroomState) => state.SessionReducer.selectedEvent);
	const showSessions = useSelector((state: GreenroomState) => state.SessionReducer.showSessions);
	const showSessionTypes = useSelector((state: GreenroomState) => state.SessionReducer.showSessionTypes);
	const showSessionsLoading = useSelector((state: GreenroomState) => state.SessionReducer.showSessionsLoading);
	const appSignedInWith = useSelector((state: GreenroomState) => state.SessionReducer.appSignedInWith);
	const awaitingProducer = useSelector((state: GreenroomState) => state.SessionReducer.awaitingProducer);
	const liveChannels = useSelector((state: GreenroomState) => state.ContentReducer.liveChannels);
	const events = useSelector((state: GreenroomState) => state.ContentReducer.events);
	const invitations = useSelector((state: GreenroomState) => state.InvitationsReducer.invitations);
	const joinRequest = useSelector((state: GreenroomState) => state.InvitationsReducer.joinRequest);
	const homeMeeting = useSelector((state: GreenroomState) => state.HomeReducer.homeMeeting);
	const selectedChannel = useSelector((state: GreenroomState) => state.HomeReducer.selectedChannel);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const shotsLoaded = useSelector((state: GreenroomState) => state.ShotsReducer.shotsLoaded);
	const { connected } = props;
	const dispatch = useDispatch();
	const { filteredMeetingsByChannel } = useHome();
	const { joinGreenroom, enterGreenroom } = useJoin();
	const { checkMediaDevices } = useMediaDevices();
	const [detailsRoute, setDetailsRoute] = useState<number>(SHOW_DETAILS_ROUTES.INFO);
	const [currentShot, setCurrentShot] = useState<number>(0);
	const joinRequestCheckInverval = useRef<NodeJS.Timeout | null>(null);
	const settingSocket = useRef<Socket | null>(null);

	const { loadShowDetails } = useLoad();

	useEffect(() => {
		if (token && homeMeeting.meeting) {
			settingSocket.current = new Socket(`meeting-${homeMeeting.meeting}`);

			settingSocket.current.onReconnected = async () => {
				loadShowDetails();
			};

			Settings(settingSocket.current);
			Shots(settingSocket.current);
			setShotReducerSocket(settingSocket.current);
			Users(settingSocket.current);
			Graphics(settingSocket.current);
			settingSocket.current.getUsers();

			if (host) {
				setSettingsReducerSocket(settingSocket.current);
				Broadcast(settingSocket.current);
				HostSync(settingSocket.current);
			}

			loadShowDetails();
		} else {
			dispatch(InvitationActions.clearInvitations());
		}
	}, []);

	useEffect(() => {
		// if a presenter is on the details page and their invite is revoked,
		// return them to home since they shouldn't be able to access this page anymore
		if (!host && !filteredMeetingsByChannel().some((event: any) => event.meeting === homeMeeting.meeting)) {
			dispatch(showDetailsBack());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, events, selectedChannel, homeMeeting.meeting]);

	useEffect(
		() => () => {
			if (joinRequestCheckInverval.current) {
				clearInterval(joinRequestCheckInverval.current);
				dispatch(InvitationActions.setJoinRequest(null)); // clear join requests when going to a different page
			}
			settingSocket.current?.disconnect();
			dispatch(HomeActions.setHomeMeeting(null));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
	 [],
	);

	useEffect(() => {
		// forcing the join request to poll AND wait for websocket in case the websocket connection is spotty or slow
		if (joinRequest) joinRequestCheckInverval.current = setInterval(() => checkJoinRequest(joinRequest), 3000);

		return () => {
			if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
		};
	}, [joinRequest]);

	useEffect(() => {
		// producer dropped while awaiting join request
		if (selectedEvent && !liveChannels.includes(selectedEvent.meeting)) {
			if (selectedEvent.entry_without_producer && joinRequestCheckInverval.current) return;
			dispatch(SessionActions.setAwaitingProducer(false));
		}
	}, [liveChannels, selectedEvent]);

	useEffect(() => {
		if (awaitingProducer && !joinRequest) {
			dispatch(SessionActions.setAwaitingProducer(false));
		}
	}, [awaitingProducer, dispatch, joinRequest]);

	async function checkJoinRequest(joinRequest: number) {
		if (joinRequest) {
			const { approval_required } = await GetRequiresApproval(token!, selectedEvent.meeting);
			if (!approval_required) dispatch(SessionActions.setAwaitingProducer(false));
			const { approved } = await CheckJoinRequest(token!, selectedEvent.meeting);
			if (approved) {
				if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
				enterGreenroom(selectedEvent);
			}
		}
	}

	const eventInfo = useMemo(() => (
			<div className="show-details-event-info">
				<EventCard
					connected={connected}
					event={homeMeeting}
					joinGreenroom={joinGreenroom}
					isWaiting={awaitingProducer}
					showSessionType={'Broadcast'}
				/>
			</div>
	), [homeMeeting, liveChannels, connected, awaitingProducer]);

	return (
		<div className="show-details-wrapper">
			<div className="show-details-above-content">
				<div className="showboard" style={!homeMeeting.meeting || !(shots.length > 0) ? cardBackground(homeMeeting, TellyCard, true, true) : undefined}>
					{homeMeeting.meeting && shots.length > 0 && (
						!shotsLoaded ? (
							<div className="loading-container">
								<WaitingIndicator />
							</div>
						) : (
							<>
								<div className="shot-thumbnail-wrapper">
									<ShotThumbnail
										borderRatio={0.7}
										shot={shots[currentShot]}
										shotThumbnailType={SHOT_THUMBNAIL_TYPES.SHOW_DETAILS}
									/>
								</div>
								<div className="showboard-controls">
									<div className="showboard-buttons">
										<button onClick={() => setCurrentShot(currentShot - 1)} disabled={!currentShot}>
											<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={10} color={COLORS.WHITE} />
										</button>
										<div className="current-shot-container">
											<span className="current-shot">{currentShot + 1}&nbsp;</span>
											<span>/ {shots.length}</span>
										</div>
										<button onClick={() => setCurrentShot(currentShot + 1)} disabled={currentShot === shots.length - 1}>
											<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={10} color={COLORS.WHITE} />
										</button>
									</div>
								</div>
							</>
						)
					)}
				</div>
				{eventInfo}
			</div>
			<div className="show-details-below-content">
				<div className="show-details-menu">
					<button className={classNames({ selected: detailsRoute === SHOW_DETAILS_ROUTES.INFO }, 'background-dark round')}
						onClick={() => setDetailsRoute(SHOW_DETAILS_ROUTES.INFO)}>
						<div className='width-full padding-left-large align-items-center justify-content-left'>
							<Icon name={ICONS.INFO_OUTLINE} color={detailsRoute === SHOW_DETAILS_ROUTES.INFO ? COLORS.WHITE : COLORS.DEFAULT_GRAY} size={14} />
							<span>Info</span>
						</div>
					</button>
					{host
						&& <button className={classNames({ selected: detailsRoute === SHOW_DETAILS_ROUTES.SETTINGS }, 'background-dark round')}
							onClick={() => setDetailsRoute(SHOW_DETAILS_ROUTES.SETTINGS)}
							disabled={!homeMeeting?.meeting}>
							<div className='width-full padding-left-large align-items-center justify-content-left'>
								<Icon name={ICONS.SETTINGS} color={detailsRoute === SHOW_DETAILS_ROUTES.SETTINGS ? COLORS.WHITE : COLORS.DEFAULT_GRAY} size={14} />
								<span>Settings</span>
							</div>
						</button>
					}
				</div>
				<div className="show-details-section-container">
					{detailsRoute === SHOW_DETAILS_ROUTES.INFO && (
						!showSessionsLoading && showSessionTypes.length > 0
							? <ShowDetailsInfo
								connected={connected}
								homeMeeting={homeMeeting}
								invitations={invitations}
								showSessions={showSessions}
								showSessionTypes={showSessionTypes}
								joinGreenroom={async (meeting: Meeting | null, fromRow?: boolean | undefined) => {
									await checkMediaDevices();
									return joinGreenroom(meeting, fromRow);
								}}
								socket={settingSocket.current}
							/>
							: <LoadingSpinner></LoadingSpinner>
					)}
					{detailsRoute === SHOW_DETAILS_ROUTES.SETTINGS
						&& <ShowDetailsSettings
							homeMeeting={homeMeeting}
							invitations={invitations}
							socket={settingSocket.current}
						/>
					}
				</div>
			</div>
		</div>
	);
}
